import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createTrigger,
  listTemplates,
  listTriggers,
  triggerEvents,
  triggerRuns,
  updateTrigger,
  type CommsTemplate,
  type PayloadCondition,
  type Trigger,
  type TriggerEvent,
  type TriggerInput,
  type TriggerRun,
} from './api';
import type { CommsKey, CommsLocale, CommsTranslate } from './i18n';
import {
  Button,
  Card,
  Checkbox,
  DataTable,
  Drawer,
  Field,
  Moment,
  Notice,
  Select,
  StatusBadge,
  TextInput,
  type Column,
} from '../../shell/kit';
import { useToast } from '../../shell/toast';
import { errorText } from '../../shell/errors';

/**
 * Сценарии «событие → шаблон → канал» (ТЗ §4.2.5, §4.4.4).
 *
 * До этого сообщение по событию было одно, захардкоженное: ответ на
 * обращение. Здесь маркетолог сам связывает событие платформы с шаблоном и
 * каналом, задаёт условия, задержку и частоту и видит каждый запуск — что
 * ушло, что отложено и почему что-то не ушло.
 */

const UNITS = [
  ['minutes', 1],
  ['hours', 60],
  ['days', 1440],
] as const;
type Unit = (typeof UNITS)[number][0];

const OPS: readonly PayloadCondition['op'][] = ['eq', 'neq', 'gte', 'lte', 'gt', 'lt', 'exists', 'missing'];
const ROLES = ['site_manager', 'network_admin', 'marketer', 'cashier', 'operator'] as const;

function splitDelay(minutes: number): { amount: number; unit: Unit } {
  const abs = Math.abs(minutes);
  if (abs !== 0 && abs % 1440 === 0) return { amount: minutes / 1440, unit: 'days' };
  if (abs !== 0 && abs % 60 === 0) return { amount: minutes / 60, unit: 'hours' };
  return { amount: minutes, unit: 'minutes' };
}

function humanDelay(t: CommsTranslate, minutes: number): string {
  if (minutes === 0) return t('comms.trig.immediately');
  const { amount, unit } = splitDelay(minutes);
  return `${amount > 0 ? '+' : ''}${amount} ${t(`comms.trig.unit.${unit}` as CommsKey)}`;
}

const EMPTY: TriggerInput = {
  name: '',
  eventKind: '',
  templateCode: '',
  channel: 'auto',
  locale: 'ru',
  audience: { type: 'customer' },
  conditions: { payload: [], sites: [], noVisitSince: false, subscriptionStatusIn: [] },
  delayMinutes: 0,
  anchorField: null,
  cooldownMinutes: 0,
  quietHours: true,
  status: 'active',
};

export function TriggersView({ t, locale }: { t: CommsTranslate; locale: CommsLocale }) {
  const toast = useToast();
  const [items, setItems] = useState<readonly Trigger[]>([]);
  const [events, setEvents] = useState<readonly TriggerEvent[]>([]);
  const [templates, setTemplates] = useState<readonly CommsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ id: string | null; input: TriggerInput } | null>(null);
  const [runs, setRuns] = useState<readonly TriggerRun[] | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = useCallback(
    (err: unknown) => {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    },
    [toast],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, catalog, tpl] = await Promise.all([listTriggers(), triggerEvents(), listTemplates()]);
      setItems(list.items);
      setEvents(catalog.events);
      setTemplates(tpl.templates);
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, [fail]);

  useEffect(() => {
    void load();
  }, [load]);

  const eventTitle = useCallback(
    (kind: string) => {
      const e = events.find((x) => x.kind === kind);
      return e ? (locale === 'kk' ? e.titleKk : e.titleRu) : kind;
    },
    [events, locale],
  );

  const openEditor = async (trigger: Trigger | null): Promise<void> => {
    setRuns(null);
    if (!trigger) {
      setEditing({ id: null, input: EMPTY });
      return;
    }
    const { id, runs30d: _runs, ...input } = trigger;
    setEditing({ id, input });
    try {
      setRuns((await triggerRuns(id)).items);
    } catch (err) {
      fail(err);
    }
  };

  const save = async (): Promise<void> => {
    if (!editing) return;
    setBusy(true);
    try {
      if (editing.id) await updateTrigger(editing.id, editing.input);
      else await createTrigger(editing.input);
      toast.ok(t('comms.trig.saved'));
      setEditing(null);
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const columns: ReadonlyArray<Column<Trigger>> = [
    {
      key: 'name',
      header: t('comms.trig.name'),
      render: (r) => (
        <div className="min-w-48">
          <div className="text-ink">{r.name}</div>
          <div className="text-xs text-ink-muted">{eventTitle(r.eventKind)}</div>
        </div>
      ),
    },
    { key: 'tpl', header: t('comms.trig.template'), render: (r) => <code className="text-xs">{r.templateCode}</code> },
    {
      key: 'aud',
      header: t('comms.trig.audience'),
      render: (r) => t(`comms.trig.audience.${r.audience.type}` as CommsKey),
    },
    {
      key: 'delay',
      header: t('comms.trig.delay'),
      render: (r) => `${humanDelay(t, r.delayMinutes)}${r.anchorField ? ` · ${r.anchorField}` : ''}`,
    },
    {
      key: 'runs',
      header: t('comms.trig.runs30'),
      render: (r) => (
        <span className="text-xs">
          {(['sent', 'scheduled', 'suppressed', 'skipped', 'failed'] as const)
            .filter((s) => (r.runs30d[s] ?? 0) > 0)
            .map((s) => `${t(`comms.trig.run.${s}` as CommsKey)}: ${r.runs30d[s]}`)
            .join(' · ') || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('comms.trig.status'),
      render: (r) => (
        <StatusBadge tone={r.status === 'active' ? 'ok' : r.status === 'paused' ? 'warn' : 'neutral'}>
          {t(`comms.trig.status.${r.status}` as CommsKey)}
        </StatusBadge>
      ),
    },
    { key: 'open', header: '', render: (r) => <Button onClick={() => void openEditor(r)}>{t('comms.trig.edit')}</Button> },
  ];

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('comms.trig.hint')}</Notice>
      <Card
        title={t('comms.trig.title')}
        actions={
          <Button tone="primary" onClick={() => void openEditor(null)}>
            {t('comms.trig.new')}
          </Button>
        }
      >
        <DataTable loading={loading} rows={items} columns={columns} empty={t('comms.common.nothing')} rowKey={(r) => r.id} />
      </Card>

      {editing ? (
        <Drawer title={editing.input.name || t('comms.trig.new')} onClose={() => setEditing(null)}>
          <TriggerForm
            t={t}
            locale={locale}
            events={events}
            templates={templates}
            value={editing.input}
            onChange={(input) => setEditing({ ...editing, input })}
          />
          <div className="mt-4">
            <Button
              tone="primary"
              disabled={busy || !editing.input.name.trim() || !editing.input.eventKind || !editing.input.templateCode}
              onClick={() => void save()}
            >
              {t('comms.trig.save')}
            </Button>
          </div>

          {editing.id ? (
            <div className="mt-6">
              <h4 className="mb-2 text-sm font-semibold text-ink">{t('comms.trig.runs')}</h4>
              <DataTable
                rows={runs ?? []}
                loading={runs === null}
                empty={t('comms.trig.runsEmpty')}
                rowKey={(r) => r.id}
                columns={[
                  { key: 'at', header: t('comms.trig.fireAt'), render: (r) => <Moment iso={r.processedAt ?? r.fireAt} /> },
                  { key: 'to', header: t('comms.trig.recipient'), render: (r) => r.recipientName ?? '—' },
                  {
                    key: 'st',
                    header: t('comms.trig.status'),
                    render: (r) => (
                      <StatusBadge
                        tone={r.status === 'sent' ? 'ok' : r.status === 'failed' || r.status === 'suppressed' ? 'danger' : 'neutral'}
                      >
                        {t(`comms.trig.run.${r.status}` as CommsKey)}
                      </StatusBadge>
                    ),
                  },
                  { key: 'why', header: t('comms.trig.reason'), render: (r) => <span className="text-xs">{r.reason ?? '—'}</span> },
                ]}
              />
            </div>
          ) : null}
        </Drawer>
      ) : null}
    </div>
  );
}

function TriggerForm({
  t,
  locale,
  events,
  templates,
  value,
  onChange,
}: {
  t: CommsTranslate;
  locale: CommsLocale;
  events: readonly TriggerEvent[];
  templates: readonly CommsTemplate[];
  value: TriggerInput;
  onChange: (next: TriggerInput) => void;
}) {
  const set = (patch: Partial<TriggerInput>): void => onChange({ ...value, ...patch });
  const event = events.find((e) => e.kind === value.eventKind);
  const fields = event?.fields ?? [];
  const codes = useMemo(() => [...new Set(templates.map((x) => x.code))].sort(), [templates]);
  const delay = splitDelay(value.delayMinutes);
  const cooldown = splitDelay(value.cooldownMinutes);
  const fieldTitle = (f: { titleRu: string; titleKk: string }): string => (locale === 'kk' ? f.titleKk : f.titleRu);

  const setCondition = (i: number, patch: Partial<PayloadCondition>): void => {
    const next = value.conditions.payload.map((c, j) => (j === i ? { ...c, ...patch } : c));
    set({ conditions: { ...value.conditions, payload: next } });
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={t('comms.trig.name')} required>
          <TextInput value={value.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label={t('comms.trig.event')} required>
          <Select value={value.eventKind} onChange={(e) => set({ eventKind: e.target.value })}>
            <option value="">—</option>
            {events.map((e) => (
              <option key={e.kind} value={e.kind}>
                {locale === 'kk' ? e.titleKk : e.titleRu} · {e.kind}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('comms.trig.template')} required>
          <Select value={value.templateCode} onChange={(e) => set({ templateCode: e.target.value })}>
            <option value="">—</option>
            {codes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('comms.trig.channel')}>
          <Select value={value.channel} onChange={(e) => set({ channel: e.target.value as TriggerInput['channel'] })}>
            <option value="auto">{t('comms.channel.auto')}</option>
            <option value="origin">{t('comms.trig.channel.origin')}</option>
            {(['sms', 'whatsapp', 'push', 'email'] as const).map((c) => (
              <option key={c} value={c}>
                {t(`comms.channel.${c}` as CommsKey)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('comms.trig.audience')}>
          <Select
            value={value.audience.type}
            onChange={(e) => {
              const type = e.target.value;
              set({
                audience:
                  type === 'employee'
                    ? { type: 'employee', field: fields[0]?.name ?? 'toEmployeeId' }
                    : type === 'role'
                      ? { type: 'role', role: 'site_manager' }
                      : { type: 'customer' },
              });
            }}
          >
            {(['customer', 'employee', 'role'] as const).map((a) => (
              <option key={a} value={a}>
                {t(`comms.trig.audience.${a}` as CommsKey)}
              </option>
            ))}
          </Select>
        </Field>
        {value.audience.type === 'employee' ? (
          <Field label={t('comms.trig.field')}>
            <TextInput
              value={value.audience.field}
              list="trigger-fields"
              onChange={(e) => set({ audience: { type: 'employee', field: e.target.value } })}
            />
          </Field>
        ) : null}
        {value.audience.type === 'role' ? (
          <Field label={t('comms.trig.role')}>
            <Select value={value.audience.role} onChange={(e) => set({ audience: { type: 'role', role: e.target.value } })}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label={t('comms.trig.locale')}>
          <Select value={value.locale} onChange={(e) => set({ locale: e.target.value as 'ru' | 'kk' })}>
            <option value="ru">{t('comms.locale.ru')}</option>
            <option value="kk">{t('comms.locale.kk')}</option>
          </Select>
        </Field>
      </div>

      <datalist id="trigger-fields">
        {fields.map((f) => (
          <option key={f.name} value={f.name}>
            {fieldTitle(f)}
          </option>
        ))}
      </datalist>

      <section className="rounded border border-line p-3">
        <p className="mb-2 text-sm font-medium text-ink">{t('comms.trig.conditions')}</p>
        {value.conditions.payload.length === 0 ? (
          <p className="mb-2 text-xs text-ink-muted">{t('comms.trig.conditionsNone')}</p>
        ) : null}
        {value.conditions.payload.map((c, i) => (
          <div key={i} className="mb-2 flex flex-wrap items-end gap-2">
            <Field label={t('comms.trig.field')}>
              <TextInput value={c.field} list="trigger-fields" onChange={(e) => setCondition(i, { field: e.target.value })} />
            </Field>
            <Field label=" ">
              <Select value={c.op} onChange={(e) => setCondition(i, { op: e.target.value as PayloadCondition['op'] })}>
                {OPS.map((op) => (
                  <option key={op} value={op}>
                    {t(`comms.trig.op.${op}` as CommsKey)}
                  </option>
                ))}
              </Select>
            </Field>
            {c.op !== 'exists' && c.op !== 'missing' ? (
              <Field label={t('comms.trig.value')}>
                <TextInput
                  value={c.value === undefined ? '' : String(c.value)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const num = Number(raw);
                    setCondition(i, { value: raw !== '' && Number.isFinite(num) ? num : raw });
                  }}
                />
              </Field>
            ) : null}
            <Button
              size="sm"
              onClick={() =>
                set({ conditions: { ...value.conditions, payload: value.conditions.payload.filter((_, j) => j !== i) } })
              }
            >
              {t('comms.trig.remove')}
            </Button>
          </div>
        ))}
        <Button
          size="sm"
          onClick={() =>
            set({
              conditions: {
                ...value.conditions,
                payload: [...value.conditions.payload, { field: fields[0]?.name ?? '', op: 'eq', value: '' }],
              },
            })
          }
        >
          {t('comms.trig.addCondition')}
        </Button>
        <label className="mt-3 flex items-center gap-2 text-sm text-ink">
          <Checkbox
            checked={value.conditions.noVisitSince}
            onChange={(e) => set({ conditions: { ...value.conditions, noVisitSince: e.target.checked } })}
          />
          {t('comms.trig.noVisitSince')}
        </label>
        {value.eventKind.startsWith('subscription.') ? (
          <label className="mt-2 flex items-center gap-2 text-sm text-ink">
            <Checkbox
              checked={value.conditions.subscriptionStatusIn.includes('active')}
              onChange={(e) =>
                set({ conditions: { ...value.conditions, subscriptionStatusIn: e.target.checked ? ['active'] : [] } })
              }
            />
            {t('comms.trig.subscriptionActive')}
          </label>
        ) : null}
      </section>

      <div className="grid gap-3 md:grid-cols-3">
        <Field label={t('comms.trig.anchor')}>
          <Select value={value.anchorField ?? ''} onChange={(e) => set({ anchorField: e.target.value || null })}>
            <option value="">{t('comms.trig.anchorEvent')}</option>
            {fields.map((f) => (
              <option key={f.name} value={f.name}>
                {fieldTitle(f)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('comms.trig.delay')} hint={t('comms.trig.delayHint')}>
          <div className="flex gap-2">
            <TextInput
              type="number"
              value={String(delay.amount)}
              onChange={(e) => {
                const unit = UNITS.find(([u]) => u === delay.unit)?.[1] ?? 1;
                set({ delayMinutes: Math.round(Number(e.target.value) * unit) });
              }}
            />
            <Select
              value={delay.unit}
              onChange={(e) => {
                const unit = UNITS.find(([u]) => u === e.target.value)?.[1] ?? 1;
                set({ delayMinutes: delay.amount * unit });
              }}
            >
              {UNITS.map(([u]) => (
                <option key={u} value={u}>
                  {t(`comms.trig.unit.${u}` as CommsKey)}
                </option>
              ))}
            </Select>
          </div>
        </Field>
        <Field label={t('comms.trig.cooldown')}>
          <div className="flex gap-2">
            <TextInput
              type="number"
              min={0}
              value={String(cooldown.amount)}
              onChange={(e) => {
                const unit = UNITS.find(([u]) => u === cooldown.unit)?.[1] ?? 1;
                set({ cooldownMinutes: Math.max(0, Math.round(Number(e.target.value) * unit)) });
              }}
            />
            <Select
              value={cooldown.unit}
              onChange={(e) => {
                const unit = UNITS.find(([u]) => u === e.target.value)?.[1] ?? 1;
                set({ cooldownMinutes: cooldown.amount * unit });
              }}
            >
              {UNITS.map(([u]) => (
                <option key={u} value={u}>
                  {t(`comms.trig.unit.${u}` as CommsKey)}
                </option>
              ))}
            </Select>
          </div>
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm text-ink">
        <Checkbox checked={value.quietHours} onChange={(e) => set({ quietHours: e.target.checked })} />
        {t('comms.trig.quietHours')}
      </label>

      <Field label={t('comms.trig.status')}>
        <Select value={value.status} onChange={(e) => set({ status: e.target.value as TriggerInput['status'] })}>
          {(['active', 'paused', 'archived'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`comms.trig.status.${s}` as CommsKey)}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}
